'use client'

import { useRouter } from 'next/navigation'
import { createContext, useCallback, useContext, useEffect, useState } from 'react'

import { useCart } from '@src/hooks/useCart'
import { useErrors } from '@src/hooks/useErrors'
import { useLoadings } from '@src/hooks/useLoadings'
import { useLocalizedRouter } from '@src/hooks/useLocalizedRouter'
import { ServerActions } from '@src/services/server-actions.service'
import { useTrackAddToCart } from '@src/utils/analytics.util'
import { EXPERIMENTS, QUERY_PARAMS } from '@src/utils/constants.util'
import { useExperimentContext } from '@src/utils/experimentProvider'
import { log } from '@src/utils/log.util'
import { createDigitalViewModel } from '@src/utils/product.digital'
import { createFitnessViewModel } from '@src/utils/product.fitness'
import { createNutritionViewModel } from '@src/utils/product.nutrition'
import {
  getAttribute,
  getFacetOptionsFromQueryParams,
  getValidQuantity,
  getVariantBySku,
  getVariantsByFacets,
  selectVariant,
  setFacetOption,
  updateURLQueryParams,
} from '@src/utils/product.util'
import { PRODUCT_TYPE_META } from '@src/utils/productMeta.util'

const PDPContext = createContext({
  productTypeMeta: PRODUCT_TYPE_META,
  productType: '' as keyof ProductTypeMeta,
  viewModel: null as ViewModel | null,
  getAttribute,
  setFacetOption: (_facetKey: string, _facetOption: FacetOption) => {},
  setQuantity: (_quantity: number) => {},
  addToCart: async () => {},
})

interface PDPProviderProps {
  children: React.ReactNode
  builderContent: any
  productData: Commercetools.ProductData
  productType: keyof ProductTypeMeta
  queryParams: URLSearchParams
  locale: string
}

export function PDPProvider({
  children,
  builderContent,
  productData,
  productType,
  queryParams: _queryParams,
  locale,
}: PDPProviderProps) {
  const [productTypeMeta] = useState(PRODUCT_TYPE_META)
  const localizedRouter = useLocalizedRouter()
  const router = useRouter()
  const trackAddToCart = useTrackAddToCart()
  const { loadings, setLoading } = useLoadings()
  const { setError } = useErrors()
  const { setCart } = useCart()
  const { allVariants, experimentClient } = useExperimentContext() ?? {}
  const cartFlyoutTest = allVariants?.[EXPERIMENTS.cartFlyOut]?.value === 'variant'

  const [viewModel, setViewModel] = useState(() => {
    let viewModel: ViewModel

    switch (productType) {
      case 'digital':
        viewModel = createDigitalViewModel({ productData, builderContent, locale })
        break
      case 'nutrition':
        viewModel = createNutritionViewModel({ productData, builderContent, locale })
        break
      case 'fitness':
        viewModel = createFitnessViewModel({ productData, builderContent, locale })
        break
      default:
        throw new Error(`Unsupported product type: ${productType}`)
    }

    const { masterVariant, facetOptions } = viewModel
    const queryParams = new URLSearchParams(_queryParams)

    // Get the quantity, sku, and facets from the query params
    const quantity = parseInt(queryParams.get(QUERY_PARAMS.quantity) ?? '1', 10)
    const sku = queryParams.get(QUERY_PARAMS.sku)
    const facets = getFacetOptionsFromQueryParams({ facetOptions, queryParams })

    // Get the variant that matches the sku and/or the variant that matches the facets
    const skuQueryVariant = getVariantBySku({ viewModel, sku })
    const [facetsQueryVariant] = getVariantsByFacets({ viewModel, facets })

    // if sku is provided, select that variant otherwise select the master variant
    if (sku) {
      const variant = skuQueryVariant ?? masterVariant
      viewModel = selectVariant({ viewModel, variantSku: variant.sku })
    }
    // if facets are provided, select the variant that matches the facets
    else if (facetsQueryVariant) {
      viewModel = selectVariant({ viewModel, variantSku: facetsQueryVariant.sku })
    }
    // otherwise select the master variant
    else {
      viewModel = selectVariant({ viewModel, variantSku: masterVariant.sku })
    }

    viewModel.quantity = getValidQuantity(quantity, productType)

    return viewModel
  })

  // Update the view model when a facet option is selected
  const _setFacetOption = useCallback(
    (facetKey: string, facetOption: FacetOption) => {
      const newViewModel = setFacetOption({
        viewModel,
        productTypeMeta,
        facetKey,
        facetOption,
      })
      setViewModel(newViewModel)
      updateURLQueryParams({ viewModel: newViewModel })
    },
    [viewModel, productTypeMeta]
  )

  // Set the quantity in the view model
  const setQuantity = useCallback(
    (quantity: number) => {
      const newViewModel = { ...viewModel, quantity: getValidQuantity(quantity, productType) }
      setViewModel(newViewModel)
      updateURLQueryParams({ viewModel: newViewModel })
    },
    [viewModel, productType]
  )

  // Add the selected variant to the cart
  const addToCart = useCallback(async () => {
    if (loadings.isAddingLineItems) return
    const sku = viewModel.selectedVariant.sku as string
    const quantity = viewModel.quantity
    try {
      setLoading({ addLineItems: { [sku]: true } })
      const cart = await ServerActions.serial.addLineItems([{ sku, quantity }])
      await trackAddToCart(cart, [{ sku, quantity }])
      setCart(cart)
      setLoading({ addLineItems: { [sku]: false } })
      experimentClient?.exposure(EXPERIMENTS.cartFlyOut)
      cartFlyoutTest ? router.push('#cart') : localizedRouter.push('/cart')
    } catch (error: any) {
      log.error(`[PDP] Error adding item to cart ${error?.message}`, error)
      setError(error)
      setLoading({ addLineItems: { [sku]: false } })
    }
  }, [
    viewModel.selectedVariant.sku,
    viewModel.quantity,
    loadings,
    router,
    setLoading,
    setError,
    setCart,
    trackAddToCart,
    cartFlyoutTest,
    localizedRouter,
    experimentClient,
  ])

  // Expose the view model to the global scope
  useEffect(() => {
    Object.assign(globalThis, { viewModel })
  }, [viewModel])

  return (
    <PDPContext.Provider
      value={{
        productTypeMeta,
        productType,
        viewModel,
        getAttribute,
        setFacetOption: _setFacetOption,
        setQuantity,
        addToCart,
      }}
    >
      {children}
    </PDPContext.Provider>
  )
}

export function usePDP() {
  try {
    return useContext(PDPContext)
  } catch (e) {
    throw new Error('usePDPViewModel must be used within a PDPViewModelProvider')
  }
}
